import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import ScrollReveal from "./ScrollReveal";
import iotSensorsImg from "@/assets/iot-sensors.webp";
import uvcModuleImg from "@/assets/uvc-module-blue.webp";
import dashboardImg from "@/assets/dashboard-preview-full.webp";


const SolutionSection = () => {
  const { t } = useTranslation();

  const pillars: {
    num: string;
    title: string;
    body: string;
    image: string;
    imageAlt: string;
    contain?: boolean;
    link?: { to: string; label: string };
  }[] = [

    {
      num: "01",
      title: t("solution.pillar1Title"),
      body: t("solution.pillar1Body"),
      image: iotSensorsImg,
      imageAlt: "Capteurs IoT Eaulistik — débit, température, pression installés sur réseau d'eau",
    },
    {
      num: "02",
      title: t("solution.pillar2Title"),
      body: t("solution.pillar2Body"),
      image: uvcModuleImg,
      imageAlt: "Module LED UVC Eaulistik — traitement anti-légionelles sans chimie",
      contain: true,
      link: {
        to: "/blog/desinfection-uv-quelle-technologie-pour-quelle-application",
        label: "Désinfection UV : quelle technologie choisir ?",
      },

    },
    {
      num: "03",
      title: t("solution.pillar3Title"),
      body: t("solution.pillar3Body"),
      image: dashboardImg,
      imageAlt: "Plateforme data Eaulistik — tableau de bord centralisé avec alertes et rapports",
    },
  ];

  return (
    <section id="solution" className="pt-0 pb-2 lg:pt-0 lg:pb-2 relative" aria-labelledby="solution-heading">
      <div className="container mx-auto px-4 space-y-6 lg:space-y-10">
        <ScrollReveal>
          <header className="space-y-4 max-w-2xl">
            <p className="section-label">{t("solution.label")}</p>
            <h2 id="solution-heading" className="font-display text-3xl lg:text-5xl text-foreground">
              {t("solution.title")}
              <br />
              <span className="text-muted-foreground text-2xl lg:text-3xl">{t("solution.subtitle")}</span>
            </h2>
            <p className="text-muted-foreground font-body text-lg">{t("solution.desc")}</p>
          </header>
        </ScrollReveal>

        <div className="grid md:grid-cols-3 gap-6" role="list">
          {pillars.map((p, i) => (
            <ScrollReveal key={i} delay={i * 0.15}>
              <article className="card-surface p-6 border-glow h-full hover:glow-cyan transition-shadow duration-300 flex flex-col" role="listitem">
                <div className="rounded-lg overflow-hidden mb-4 border border-primary/10 bg-card">
                  <img
                    src={p.image}
                    alt={p.imageAlt}
                    className={`w-full h-32 ${p.contain ? "object-contain bg-card" : "object-cover"}`}
                    loading="lazy"
                    width="400"
                    height="128"
                  />
                </div>
                <span className="font-mono text-xs text-gold">{t("solution.pillar")} {p.num}</span>
                <h3 className="font-display text-xl text-foreground mt-1 mb-2">{p.title}</h3>
                <p className="text-sm text-muted-foreground font-body leading-relaxed">{p.body}</p>
                {p.link && (
                  <Link
                    to={p.link.to}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary font-body hover:underline"
                  >
                    {p.link.label} <ArrowRight size={14} />
                  </Link>
                )}
              </article>

            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal>
          <figure className="max-w-xl mx-auto">
            <blockquote className="text-center text-muted-foreground italic font-body text-lg leading-relaxed">
              {t("solution.quote")}
            </blockquote>
            <figcaption className="text-center text-sm mt-2 text-muted-foreground">{t("solution.quoteAuthor")}</figcaption>
          </figure>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default SolutionSection;
